<script setup lang="ts">
import { Popover, PopoverOverlay, PopoverPanel } from "@headlessui/vue";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useToast } from "vue-toastification";

import LeDictionarySelectorButton from "@/components/dictionary-selector/LeDictionarySelectorButton.vue";
import LeDictionarySelectorItem from "@/components/dictionary-selector/LeDictionarySelectorItem.vue";
import { useComponentScope, useRestoreScroll } from "@/composables";
import { MatomoHelper } from "@/helpers";
import { type Dictionary, type DictionaryGroupWithDictionaries, MatomoEventAction, MatomoEventCategory } from "@/types";

interface PopoverPosition {
  top: string;
  left: string;
}

interface Props {
  hasLoaded?: boolean;
  companyDictionaries?: Dictionary[];
  companyDictionaryGroupHeading: string;
  favoriteDictionaries?: Dictionary[];
  resultDictionaries?: Dictionary[];
  selectedDictionary?: Dictionary;
  groupsWithDictionaries?: DictionaryGroupWithDictionaries[];
  popoverPosition?: PopoverPosition;
  count?: "dictionaries" | "results";
  showAllDictionariesOption?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  companyDictionaries: () => [],
  favoriteDictionaries: () => [],
  resultDictionaries: () => [],
  selectedDictionary: undefined,
  groupsWithDictionaries: () => [],
  popoverPosition: () => ({ top: "0", left: "100%" }),
  count: "results",
  showAllDictionariesOption: false
});

const emit = defineEmits<{
  select: [dictionaryId: string];
  favorite: [dictionaryId: string];
  unfavorite: [dictionaryId: string];
}>();

const toast = useToast();
const { t } = useI18n();
const { scope } = useComponentScope();

const popoverContent = ref<HTMLElement>();
const popoverContentTop = computed(() => `${popoverContent.value?.getBoundingClientRect().top}px`);

const { restoreScroll } = useRestoreScroll(popoverContent.value);

const selectorButtonBadge = computed<number | null>(() => {
  if (props.count === "dictionaries") {
    return (
      props.companyDictionaries.length +
      props.groupsWithDictionaries.reduce<number>(
        (acc, group: DictionaryGroupWithDictionaries) => acc + group.dictionaries.length,
        0
      )
    );
  }

  return props.hasLoaded ? props.resultDictionaries.length : null;
});

const isSelected = (dictionaryId: string): boolean => {
  if (dictionaryId === "all-dictionaries") {
    return props.selectedDictionary === undefined;
  }

  return dictionaryId === props.selectedDictionary?.id;
};
const isFavorite = (dictionaryId: string): boolean => {
  const favoriteDictionary = props.favoriteDictionaries.find((dictionary) => dictionary.id === dictionaryId);

  return favoriteDictionary !== undefined;
};
const hasResult = (dictionaryId: string) => props.resultDictionaries.find((d) => d.id === dictionaryId) !== undefined;

const favorite = (dictionaryId: string) => {
  MatomoHelper.sendEvent(MatomoEventCategory.AllDictionaries, MatomoEventAction.AddFavorite, "Added favorite");

  restoreScroll(() => {
    emit("favorite", dictionaryId);
  });
};

const unfavorite = (dictionaryId: string) => {
  MatomoHelper.sendEvent(MatomoEventCategory.AllDictionaries, MatomoEventAction.RemoveFavorite, "Removed favorite");

  restoreScroll(() => {
    emit("unfavorite", dictionaryId);
  });
};

const select = (dictionaryId: string, closeFn?: () => void) => {
  if (dictionaryId === "all-dictionaries") {
    emit("select", "");
    closeFn?.();
    return;
  }

  MatomoHelper.sendEvent(
    MatomoEventCategory.AllDictionaries,
    MatomoEventAction.SelectDictionaryAll,
    `${hasResult(dictionaryId) ? "Dictionary with result" : "Dictionary with no result"}`
  );

  toast.clear();
  emit("select", dictionaryId);
  closeFn?.();
};

const clickedAllDictionariesMatomoTracker = (isOpen: boolean) => {
  // We dont track close action
  if (isOpen) {
    return;
  }

  MatomoHelper.sendEvent(
    MatomoEventCategory.AllDictionaries,
    MatomoEventAction.OpenAllDictionaries,
    "Clicked all dictionaries"
  );
};
</script>

<template>
  <Popover v-slot="{ open, close }" :[scope]="scope" class="le-dictionaries-selector">
    <Transition name="le-dictionaries-selector__overlay-transition">
      <PopoverOverlay class="le-dictionaries-selector__overlay" />
    </Transition>
    <LeDictionarySelectorButton
      :is-active="open"
      :badge="selectorButtonBadge"
      @click="clickedAllDictionariesMatomoTracker(open)"
    >
      <slot name="buttonLabel">
        {{ t("le-dictionary-selector.selector-button") }}
      </slot>
    </LeDictionarySelectorButton>

    <Transition name="le-dictionaries-selector__popover-transition">
      <PopoverPanel class="le-dictionaries-selector__popover" focus>
        <div ref="popoverContent" class="le-dictionaries-selector__popover-content">
          <div v-if="props.showAllDictionariesOption" class="le-dictionaries-selector__collection">
            <h3 class="le-dictionaries-selector__collection-header">
              {{ t("le-dictionary-selector.selector-button") }}
            </h3>
            <LeDictionarySelectorItem
              class="le-dictionaries-selector__item"
              :dictionary="{
                id: 'all-dictionaries',
                title: t('le-dictionary-selector.selector-button'),
                subtitle: '',
                brandColor: '585a58',
                publisherLetter: 'A',
                auto: false,
                publisher: t('le-dictionary-selector.deselector-button'),
                publisherKey: 'AllDictionaries',
                description: '',
                intro: '',
                groupHeading: null,
                isbns: [],
                hasAbout: false,
                isUpsell: false,
                metadata: [],
                methods: [],
                searchPriority: 999999999,
                tags: []
              }"
              :is-selected="isSelected('all-dictionaries')"
              @select="select('all-dictionaries', close)"
            />
          </div>

          <div v-if="props.companyDictionaries.length > 0" class="le-dictionaries-selector__collection">
            <h3 class="le-dictionaries-selector__collection-header">
              {{ props.companyDictionaryGroupHeading }}
            </h3>
            <LeDictionarySelectorItem
              v-for="dictionary in props.companyDictionaries"
              :key="dictionary.id"
              class="le-dictionaries-selector__item"
              :dictionary="dictionary"
              :is-selected="isSelected(dictionary.id)"
              :has-result="hasResult(dictionary.id)"
              @select="select(dictionary.id, close)"
            />
          </div>

          <div v-if="props.favoriteDictionaries.length > 0" class="le-dictionaries-selector__collection">
            <h3 class="le-dictionaries-selector__collection-header">
              {{ t("le-dictionary-selector.favorites-collection.header") }}
            </h3>
            <LeDictionarySelectorItem
              v-for="dictionary in props.favoriteDictionaries"
              :key="dictionary.id"
              show-favorite-toggle
              class="le-dictionaries-selector__item"
              is-favorite
              :dictionary="dictionary"
              :is-selected="isSelected(dictionary.id)"
              :has-result="hasResult(dictionary.id)"
              @unfavorite="unfavorite(dictionary.id)"
              @select="select(dictionary.id, close)"
            />
          </div>

          <div v-if="props.groupsWithDictionaries.length > 0" class="le-dictionaries-selector__collection">
            <h3 class="le-dictionaries-selector__collection-header">
              {{ t("le-dictionary-selector.dictionaries-collection.header") }}
            </h3>
            <template v-for="group in props.groupsWithDictionaries" :key="group.id">
              <div v-if="group.dictionaries.length > 0" class="le-dictionaries-selector__language-group">
                <h4 class="le-dictionaries-selector__language-group-header">
                  {{ group.title }}
                </h4>
                <LeDictionarySelectorItem
                  v-for="dictionary in group.dictionaries"
                  :key="dictionary.id"
                  show-favorite-toggle
                  class="le-dictionaries-selector__item"
                  :dictionary="dictionary"
                  :is-selected="isSelected(dictionary.id)"
                  :is-favorite="isFavorite(dictionary.id)"
                  :has-result="hasResult(dictionary.id)"
                  @favorite="favorite(dictionary.id)"
                  @unfavorite="unfavorite(dictionary.id)"
                  @select="select(dictionary.id, close)"
                />
              </div>
            </template>
          </div>
        </div>
      </PopoverPanel>
    </Transition>
  </Popover>
</template>

<style lang="less" scoped>
@import "@dsk/styles/all";
@import "@/assets/styles/z-index.less";

.le-dictionaries-selector {
  position: relative;

  &[data-headlessui-state="open"] {
    z-index: @le-dictionary-selector__open-z-index;
  }

  &__overlay {
    position: fixed;
    z-index: @le-dictionary-selector__overlay-z-index;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    opacity: 0.4;
    background-color: @colors[@neutrals][300];

    &-transition-enter-active,
    &-transition-leave-active {
      transition: opacity 150ms ease;
    }

    &-transition-enter-from,
    &-transition-leave-to {
      opacity: 0;
    }
  }

  &__popover {
    position: absolute;
    z-index: @le-dictionary-selector__popover-z-index;
    top: v-bind("props.popoverPosition.top");
    left: v-bind("props.popoverPosition.left");
    min-width: 500px;
    margin-left: 30px;
    border: 1px solid @colors[@secondary][600];
    border-radius: 4px;
    outline: none;
    background-color: @colors[@neutrals][100];
    box-shadow: @shadows[floating-default];
    font-size: 12px;

    &-content {
      max-height: calc(100vh - v-bind("popoverContentTop") - 90px);
      padding: 0 30px 20px 30px;
      overflow: auto;
      overscroll-behavior: contain;
    }

    &-transition-enter-active,
    &-transition-leave-active {
      transition: all 100ms ease-out;
    }

    &-transition-enter-from,
    &-transition-leave-to {
      transform: translateX(-30px);
      opacity: 0;
    }
  }

  &__collection {
    &-header {
      position: sticky;
      z-index: @le-dictionary-selector__collection-header-z-index;
      top: 0px;
      margin-top: 12px;
      margin-bottom: 8px;
      padding-top: 20px;
      padding-bottom: 8px;
      border-bottom: 2px solid @colors[@secondary][400];
      background-color: @colors[@neutrals][100];
      color: @colors[@neutrals][400];
      font-family: @font-family-heading;
      font-size: 20px;
      font-weight: 500;
    }
  }

  &__language-group {
    &:not(:last-child) {
      margin-bottom: 14px;
    }

    &-header {
      position: sticky;
      z-index: @le-dictionary-selector__language-group-header-z-index;
      top: 52px;
      margin-top: 0;
      margin-bottom: 0;
      padding-top: 14px;
      padding-bottom: 8px;
      background-color: @colors[@neutrals][100];
      color: @colors[@neutrals][400];
      font-family: @font-family-body;
      font-size: 16px;
      font-weight: 600;
      mask-image: linear-gradient(to bottom, black calc(100% - 10px), transparent 100%);
    }
  }

  &__item + &__item {
    margin-top: 8px;
  }
}
</style>
